/// <amd-module name="Repos/CommitsList/CommitsDataProviderService" />

import * as Resources from "Repos/CommitsList/Resources";
import {
    ChangeListSearchCriteria,
    GitAnnotatedTag,
    GitCommitRef,
    GitHistoryMode,
    GitPullRequest,
    GitPush,
    GitQueryCommitsCriteria,
    GitUserDate,
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType
} from "Repos/Common/Contracts/Git";
import { ICommit, WebClientGraphRow } from "Repos/Common/Contracts/History";
import { IVersionControlViewModel } from "Repos/Common/Contracts/VersionControlViewModel";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { createTag, getDefaultCreateBranchAction } from "Repos/Common/ReposActionService";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { isEmptyObjectId } from "Repos/Common/Util/Ref";
import { commitToVersionDescriptor, getVersionString, gitVersionStringToVersionDescriptor, tagToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { ICreateBranchDialogProps, ICreateTagDialogProps } from "Repos/VersionDialogs/Dialogs.Props";
import { IReadonlyObservableArray, ObservableCollection } from "VSS/Core/Observable";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { getServiceLocation } from "VSS/Platform/Location";
import { copyToClipboard } from "VSSUI/Clipboard";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { IPageStateHistoryService } from "VSS/Features/History";

export interface ICommitsDataProviderService extends IVssService {
    fetchCommitsForPush(push: GitPush, skip: number, top: number): Promise<GitCommitSearchResultsExtended | undefined>;
    fetchCommits(filterData: CommitsSearchFilterData, version: GitVersionDescriptor): Promise<GitCommitSearchResultsExtended | undefined>;
    fetchCommitsFrom(partialCommitId: string, skip: number): Promise<GitCommitSearchResultsExtended | undefined>;
    fetchCommitDetails(commitIds: string[], version: GitVersionDescriptor): Promise<GitCommitSearchResultsExtended | undefined>;
    fetchTags(commit: string): Promise<GitCommitTagRef[] | undefined>;
    getCommitContributionContext(
        commitId: string,
        comment: string,
        commentTruncated: boolean,
        repositoryId: string,
        path?: string
    ): ICommitContributionContext;
    getCommitMenuItems(commit: ICommit, onDismiss: () => void): IReadonlyObservableArray<IMenuItem>;
    getInitialCommits(): GitCommitSearchResults | undefined;
    getCommitUrl(commitId: string, refName?: string, filePath?: string, action?: string): string;
    getCommitsUrl(version: GitVersionDescriptor): string;
}

/**
 * Properties passed to contributed actions applicable to commits.
 */
export interface ICommitContributionContext {
    /**
     * Base URL for the organization
     */
    serviceBaseUrl: string;

    /**
     * Details about the commit for which the menu applies to
     */
    commit: {
        commitId: string;
        comment: string;
        commentTruncated: boolean;
        repositoryId: string;
    };

    /**
     * Path filter if looking at a specific file in a commit
     */
    path?: string;
}

namespace GraphSettingsConstants {
    export const GraphRowLimit = 1000;
    export const EmptyLineLengthLimit = 20;
    export const GraphColumnDefaultWidth = 80;
}

interface GitHistorySearchCriteria extends ChangeListSearchCriteria {
    gitRenameVersion?: GitVersionDescriptor;
    gitLogHistoryMode?: GitHistoryMode;
    alias: string;
}

const enum CommitOrder {
    dateOrder,
    topoOrder
}

interface GitHistoryQueryArguments {
    startFromVersion: GitVersionDescriptor;
    notInVersion: GitVersionDescriptor;
    order: CommitOrder;
    path: string;
    historyMode: GitHistoryMode;
    stopAtAdds: boolean;
    fromDate: string;
    toDate: string;
    author: string;
    committer: string;
    skip: number;
    maxResultCount: number;
}

interface GitHistoryDataOptions {
    fetchBuildStatuses: boolean;
    fetchGraph: boolean;
    fetchPullRequests: boolean;
    fetchTags: boolean;
}

interface GitArtifactsQueryArguments {
    fetchBuildStatuses: boolean;
    fetchPullRequests: boolean;
    fetchTags: boolean;
    startFromVersion: GitVersionDescriptor | undefined;
    commitIds: string[];
}

interface GitCommitLookupArguments {
    fromCommitId: string;
    toCommitId: string;
    skip: number;
    maxResultCount: number;
}

interface GitGraphQueryArguments {
    fetchGraph: boolean;
    emptyLineLengthLimit: number;
    emptyLineLengthMultiplier: boolean;
    order: CommitOrder;
}

interface GitHistoryDataProviderArguments {
    gitCommitLookupArguments: GitCommitLookupArguments | undefined;
    gitHistoryQueryArguments: GitHistoryQueryArguments | undefined;
    gitArtifactsQueryArguments: GitArtifactsQueryArguments | undefined;
    gitGraphQueryArguments: GitGraphQueryArguments | undefined;
}

interface TfsAuthorIdentity {
    displayName: string;
    alias: string;
}

export interface GitCommitSearchResults {
    commits: GitCommitRef[];
    graphRows: WebClientGraphRow[];
    startingCommitId: string;
    hasMore: boolean;
    stillProcessing: boolean;
}

export interface GitCommitSearchResultsExtended extends GitCommitSearchResults {
    isGitGraphFeatureEnabled: boolean;
    project: string;
    pullRequests: {
        [key: string]: GitPullRequest;
    };
    repository: string;
    repositoryName: string;
    searchCancelled: boolean;
    tags: { [commitId: string]: GitCommitTagRef[] };
}

export interface GitCommitTagRef {
    comment: string;
    name: string;
    objectId: string;
    resolvedCommitId: string;
    tagger: GitUserDate;
}

export interface CommitsSearchFilterData {
    committerId: string | undefined;
    committerName: string | undefined;
    compareVersion: string | undefined;
    filePath?: string;
    historyMode: GitHistoryMode | undefined;
    itemVersion: string | undefined;
    gitRenameVersion: GitVersionDescriptor | undefined;
    fromDate: Date | undefined;
    toDate: Date | undefined;
    skip: number | undefined;
}

const commitsDataProviderKey = "ms.vss-code-web.commits-data-provider";

class CommitsDataProviderService extends VssService implements ICommitsDataProviderService {
    private latestRequestKey: number = 0;
    private vcViewModel: IVersionControlViewModel | undefined;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        this.vcViewModel = vcViewModelDataProviderService.getViewModel();
    }

    public async fetchCommitsForPush(push: GitPush, skip: number, top: number): Promise<GitCommitSearchResultsExtended | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        if (this.vcViewModel) {
            const searchCriteria = this.getSearchCriteria(push, skip, top);
            this.latestRequestKey++;
            const requestKey = this.latestRequestKey;
            const dataOptions: GitHistoryDataOptions = {
                fetchBuildStatuses: false,
                fetchPullRequests: false,
                fetchTags: true,
                fetchGraph: false
            };

            const data = await contributionService.getDataAsync<GitCommitSearchResultsExtended>(
                commitsDataProviderKey,
                { searchCriteria: this.getDataProviderArguments(searchCriteria, dataOptions), repositoryId: this.vcViewModel.gitRepository!.id },
                true
            );

            if (data) {
                data.searchCancelled = !(this.latestRequestKey == requestKey);
                data.project = this.vcViewModel.gitRepository!.project.name;
                data.repository = this.vcViewModel.gitRepository!.id;
            }
            return data;
        }

        return undefined;
    }

    public async fetchCommitDetails(commitIds: string[], version: GitVersionDescriptor): Promise<GitCommitSearchResultsExtended | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        if (this.vcViewModel) {
            const searchCriteria: GitHistoryDataProviderArguments = {
                gitCommitLookupArguments: undefined,
                gitHistoryQueryArguments: undefined,
                gitArtifactsQueryArguments: this.getArtifactsSearchArguments(commitIds, version),
                gitGraphQueryArguments: undefined
            };
            this.latestRequestKey++;
            const requestKey = this.latestRequestKey;

            const data = await contributionService.getDataAsync<GitCommitSearchResultsExtended>(
                commitsDataProviderKey,
                { searchCriteria: searchCriteria, repositoryId: this.vcViewModel.gitRepository!.id },
                true
            );

            if (data) {
                data.searchCancelled = !(this.latestRequestKey == requestKey);
                data.project = this.vcViewModel.gitRepository!.project.name;
                data.repository = this.vcViewModel.gitRepository!.id;
                data.repositoryName = this.vcViewModel.gitRepository!.name;
            }
            return data;
        }

        return undefined;
    }

    public async fetchCommits(
        filterData: CommitsSearchFilterData,
        version: GitVersionDescriptor
    ): Promise<GitCommitSearchResultsExtended | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        if (this.vcViewModel) {
            const searchCriteria = this.getSearchCriteriaForBranch(filterData, version);

            this.latestRequestKey++;
            const requestKey = this.latestRequestKey;
            const dataOptions: GitHistoryDataOptions = {
                fetchBuildStatuses: true,
                fetchPullRequests: true,
                fetchTags: true,
                fetchGraph: true
            };

            const data = await contributionService.getDataAsync<GitCommitSearchResultsExtended>(
                commitsDataProviderKey,
                { searchCriteria: this.getDataProviderArguments(searchCriteria, dataOptions), repositoryId: this.vcViewModel.gitRepository!.id },
                true
            );

            if (data) {
                data.searchCancelled = !(this.latestRequestKey == requestKey);
                data.project = this.vcViewModel.gitRepository!.project.name;
                data.repository = this.vcViewModel.gitRepository!.id;
            }

            return data;
        }

        return undefined;
    }

    public async fetchCommitsFrom(partialCommitId: string, skip: number): Promise<GitCommitSearchResultsExtended | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        if (this.vcViewModel) {
            const searchCriteria: GitHistoryDataProviderArguments = {
                gitCommitLookupArguments: this.getCommitLookupArguments(partialCommitId, skip),
                gitHistoryQueryArguments: undefined,
                gitArtifactsQueryArguments: undefined,
                gitGraphQueryArguments: undefined
            };
            this.latestRequestKey++;
            const requestKey = this.latestRequestKey;

            const data = await contributionService.getDataAsync<GitCommitSearchResultsExtended>(
                commitsDataProviderKey,
                { searchCriteria: searchCriteria, repositoryId: this.vcViewModel.gitRepository!.id },
                true
            );

            if (data) {
                data.searchCancelled = !(this.latestRequestKey == requestKey);
                data.project = this.vcViewModel.gitRepository!.project.name;
                data.repository = this.vcViewModel.gitRepository!.id;
                data.repositoryName = this.vcViewModel.gitRepository!.name;
            }
            return data;
        }

        return undefined;
    }

    public async fetchTags(commitId: string): Promise<GitCommitTagRef[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        if (this.vcViewModel) {
            const dataProviderArgs: GitHistoryDataProviderArguments = {
                gitCommitLookupArguments: undefined,
                gitHistoryQueryArguments: undefined,
                gitArtifactsQueryArguments: {
                    fetchBuildStatuses: false,
                    fetchPullRequests: false,
                    fetchTags: true,
                    startFromVersion: undefined,
                    commitIds: [commitId]
                },
                gitGraphQueryArguments: undefined
            };

            const data = await contributionService.getDataAsync<GitCommitSearchResultsExtended>(
                commitsDataProviderKey,
                { searchCriteria: dataProviderArgs, repositoryId: this.vcViewModel.gitRepository!.id },
                true
            );

            return data && data.tags && data.tags[commitId] ? data.tags[commitId] : [];
        }

        return undefined;
    }

    public getCommitContributionContext(
        commitId: string,
        comment: string,
        commentTruncated: boolean,
        repositoryId: string,
        path?: string
    ): ICommitContributionContext {
        // Best-effort for base service URL. Don't blow up the page if not seeded appropriately.
        let serviceBaseUrl = "";
        try {
            serviceBaseUrl = getServiceLocation(this.pageContext, undefined);
        } catch {}

        return {
            serviceBaseUrl,
            commit: {
                comment,
                commentTruncated,
                commitId,
                repositoryId
            },
            path
        };
    }

    public getCommitMenuItems(item: ICommit, onDismiss: () => void): IReadonlyObservableArray<IMenuItem> {
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        const historyState = pageStateHistoryService.getProjectedState();

        const browseFileUrl = reposRoutingService.getFilesUrl(
            this.vcViewModel ? this.vcViewModel.gitRepository!.name : "",
            this.vcViewModel ? this.vcViewModel.gitRepository!.project.name : "",
            historyState["path"],
            commitToVersionDescriptor(item.commit.commitId)
        );

        const permissionService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");
        const canCreateBranch = permissionService.hasPermission(GitRepositoryPermissions.CreateBranch);
        const canCreateTag = permissionService.hasPermission(GitRepositoryPermissions.CreateTag);

        const friendlyShortCommitId = item.shortCommitId.split(" ").join("");

        const menuItems: IMenuItem[] = [
            {
                iconProps: { iconName: "Copy" },
                id: "copySha",
                onActivate: () => {
                    copyToClipboard(item.commit.commitId);
                },
                text: Resources.MoreButtonOptionCopy
            },
            {
                id: "filePath",
                text: Resources.MoreButtonOptionBrowse,
                iconProps: { iconName: "Search" },
                onActivate: () => {
                    FastPageSwitch(this.pageContext, browseFileUrl, true);
                }
            }
        ];

        if (canCreateBranch || canCreateTag) {
            menuItems.push({
                id: "dividerOne",
                itemType: MenuItemType.Divider
            });
        }

        if (canCreateBranch) {
            menuItems.push({
                id: "createBranch",
                text: Resources.MoreButtonOptionNewBranch,
                iconProps: { iconName: "BranchMerge" },
                onActivate: getDefaultCreateBranchAction(this.pageContext, {
                    branchName: `branch-from-${friendlyShortCommitId}`,
                    onDismiss,
                    referenceVersion: {
                        version: item.commit.commitId,
                        versionOptions: GitVersionOptions.None,
                        versionType: GitVersionType.Commit
                    }
                } as ICreateBranchDialogProps)
            });
        }

        if (canCreateTag) {
            menuItems.push({
                id: "createTag",
                text: Resources.MoreButtonOptionCreateTag,
                iconProps: { iconName: "Tag" },
                onActivate: () => {
                    createTag(this.pageContext, {
                        tagName: `tag-from-${friendlyShortCommitId}`,
                        onComplete: (createdTag: GitAnnotatedTag): void => {
                            const tagUrl = reposRoutingService.getFilesUrl(
                                this.vcViewModel ? this.vcViewModel.gitRepository!.name : "",
                                this.vcViewModel ? this.vcViewModel.gitRepository!.project.name : "",
                                undefined,
                                tagToVersionDescriptor(createdTag.name)
                            );
                            FastPageSwitch(this.pageContext, tagUrl, true);
                        },
                        onDismiss,
                        referenceVersion: {
                            version: item.commit.commitId,
                            versionOptions: GitVersionOptions.None,
                            versionType: GitVersionType.Commit
                        },
                        repository: this.vcViewModel && this.vcViewModel.gitRepository
                    } as ICreateTagDialogProps);
                }
            });
        }

        const menuItemCollection = new ObservableCollection<IMenuItem>();
        menuItemCollection.push(menuItems);
        addMenuItems(
            this.pageContext,
            menuItemCollection,
            "ms.vss-code-web.git-commit-list-menu",
            this.getCommitContributionContext(item.commit.commitId, item.commit.comment, item.commit.commentTruncated, item.repositoryId)
        );
        return menuItemCollection;
    }

    public getInitialCommits(): GitCommitSearchResults | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getData<GitCommitSearchResults>(commitsDataProviderKey);
    }

    public getCommitUrl(commitId: string, refName?: string, filePath?: string, action?: string): string {
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        return reposRoutingService.getCommitUrl(
            this.vcViewModel ? this.vcViewModel.gitRepository!.name : "",
            this.vcViewModel ? this.vcViewModel.gitRepository!.project.name : "",
            commitId,
            refName,
            filePath,
            action
        );
    }

    public getCommitsUrl(version: GitVersionDescriptor): string {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        return reposRoutingService.getCommitsUrl(
            this.vcViewModel ? this.vcViewModel.gitRepository!.name : "",
            this.vcViewModel ? this.vcViewModel.gitRepository!.project.name : "",
            version
        );
    }

    public getGitGraphQueryArguments(
        queryCriteria: GitQueryCommitsCriteria,
        dataOptions: GitHistoryDataOptions,
        isFetchingRenameHistory?: boolean
    ): GitGraphQueryArguments {
        const skip = queryCriteria.$skip || 0;
        const fetchGraphDataOption = dataOptions && dataOptions.fetchGraph;

        // if (isFetchingRenameHistory === undefined) {
        //     isFetchingRenameHistory = this._getAggregatedState().historyListState.fetchRenameHistoryCalled;
        // }

        return {
            fetchGraph: true, // skip < GraphSettingsConstants.GraphRowLimit && fetchGraphDataOption && !isFetchingRenameHistory,
            emptyLineLengthLimit: GraphSettingsConstants.EmptyLineLengthLimit,
            emptyLineLengthMultiplier: false,
            order: fetchGraphDataOption ? CommitOrder.topoOrder : CommitOrder.dateOrder
        };
    }

    private getAuthorfromTFSIdentity(tfsAuthorIdentity: TfsAuthorIdentity): string {
        const authorString: string =
            (tfsAuthorIdentity.displayName ? tfsAuthorIdentity.displayName : "") +
            (tfsAuthorIdentity.alias ? " <" + tfsAuthorIdentity.alias + ">" : "");

        return authorString;
    }

    private getSearchCriteria(push: GitPush, skip: number, top: number): GitHistorySearchCriteria {
        const refUpdate = push.refUpdates[0];
        const searchCriteria = {
            itemVersion: "GC" + (isEmptyObjectId(refUpdate.oldObjectId) ? refUpdate.newObjectId : refUpdate.oldObjectId),
            compareVersion: isEmptyObjectId(refUpdate.oldObjectId) ? "" : "GC" + refUpdate.newObjectId,
            top: top,
            toDate: "",
            fromDate: "",
            alias: "",
            excludeDeletes: false,
            followRenames: false,
            fromVersion: "",
            itemPath: "",
            skip: skip,
            toVersion: "",
            user: "",
            itemPaths: []
        };

        searchCriteria.user = this.getAuthorfromTFSIdentity({
            alias: searchCriteria.alias,
            displayName: searchCriteria.user
        });

        return searchCriteria;
    }

    private getArtifactsSearchArguments(commitIds: string[], version: GitVersionDescriptor): GitArtifactsQueryArguments {
        const searchCriteria: GitArtifactsQueryArguments = {
            commitIds: commitIds,
            startFromVersion: version,
            fetchBuildStatuses: true,
            fetchPullRequests: true,
            fetchTags: true
        };

        return searchCriteria;
    }

    private getSearchCriteriaForBranch(filterData: CommitsSearchFilterData, version: GitVersionDescriptor): GitHistorySearchCriteria {
        const searchCriteria = {
            itemVersion: filterData.itemVersion || getVersionString(version),
            compareVersion: filterData.compareVersion || "",
            top: 50,
            toDate: filterData.toDate ? filterData.toDate.toISOString() : "",
            fromDate: filterData.fromDate ? filterData.fromDate.toISOString() : "",
            gitRenameVersion: filterData.gitRenameVersion,
            gitLogHistoryMode: filterData.historyMode,
            alias: "",
            excludeDeletes: false,
            followRenames: false,
            fromVersion: "",
            itemPath: filterData.filePath ? filterData.filePath : "/",
            skip: filterData.skip || 0,
            toVersion: "",
            user: "",
            itemPaths: []
        };

        if (filterData.committerId) {
            searchCriteria.user = filterData.committerId;
        }

        return searchCriteria;
    }

    private getCommitLookupArguments(partialCommitId: string, skip: number): GitCommitLookupArguments {
        let fromCommit = "0000000000000000000000000000000000000000";
        let toCommit = "ffffffffffffffffffffffffffffffffffffffff";

        fromCommit = partialCommitId.concat(fromCommit.substr(partialCommitId.length - 1, fromCommit.length - partialCommitId.length));
        toCommit = partialCommitId.concat(toCommit.substr(partialCommitId.length - 1, toCommit.length - partialCommitId.length));
        const queryCriteria: GitCommitLookupArguments = {
            fromCommitId: fromCommit,
            toCommitId: toCommit,
            skip: skip,
            maxResultCount: 25
        };

        return queryCriteria;
    }

    private getQueryCriteria(searchCriteria: GitHistorySearchCriteria): GitQueryCommitsCriteria {
        const queryCriteria: GitQueryCommitsCriteria = {
            author: searchCriteria.user,
            fromDate: searchCriteria.fromDate,
            toDate: searchCriteria.toDate,
            fromCommitId: searchCriteria.fromVersion,
            toCommitId: searchCriteria.toVersion,
            itemPath: searchCriteria.itemPath,
            excludeDeletes: searchCriteria.excludeDeletes,
            user: "",
            $top: searchCriteria.top,
            $skip: searchCriteria.skip
        } as GitQueryCommitsCriteria;

        if (searchCriteria.itemVersion) {
            queryCriteria.itemVersion = gitVersionStringToVersionDescriptor(searchCriteria.itemVersion);
        }
        if (searchCriteria.compareVersion) {
            queryCriteria.compareVersion = gitVersionStringToVersionDescriptor(searchCriteria.compareVersion);
        }

        if (searchCriteria.gitRenameVersion) {
            queryCriteria.itemVersion = searchCriteria.gitRenameVersion;
        }

        return queryCriteria;
    }

    private getDataProviderArguments(
        searchCriteria: GitHistorySearchCriteria,
        dataOptions: GitHistoryDataOptions,
        isFetchingRenameHistory?: boolean
    ): GitHistoryDataProviderArguments {
        const gitDataProviderArguments: GitHistoryDataProviderArguments = {
            gitCommitLookupArguments: undefined,
            gitHistoryQueryArguments: undefined,
            gitArtifactsQueryArguments: undefined,
            gitGraphQueryArguments: undefined
        };

        if (searchCriteria && dataOptions) {
            const queryCriteria = this.getQueryCriteria(searchCriteria);
            const gitHistoryMode = searchCriteria.gitLogHistoryMode || GitHistoryMode.SimplifiedHistory;
            const includeCommitArgs = !!queryCriteria.fromCommitId || !!queryCriteria.toCommitId;
            let includeHistoryArgs = true;
            if (includeCommitArgs) {
                includeHistoryArgs = false;
            }
            gitDataProviderArguments.gitHistoryQueryArguments = includeHistoryArgs
                ? this.getGitHistoryQueryArguments(queryCriteria, gitHistoryMode)
                : undefined;
            gitDataProviderArguments.gitGraphQueryArguments = this.getGitGraphQueryArguments(queryCriteria, dataOptions, isFetchingRenameHistory);

            gitDataProviderArguments.gitArtifactsQueryArguments = dataOptions.fetchPullRequests
                ? this.getGitArtifactsQueryArguments(queryCriteria, dataOptions)
                : undefined;
        }
        return gitDataProviderArguments;
    }

    private getGitHistoryQueryArguments(
        queryCriteria: GitQueryCommitsCriteria,
        historyMode: GitHistoryMode = GitHistoryMode.SimplifiedHistory
    ): GitHistoryQueryArguments {
        const gitHistoryArgs: GitHistoryQueryArguments = {
            startFromVersion: queryCriteria.itemVersion,
            notInVersion: queryCriteria.compareVersion,
            order: CommitOrder.dateOrder,
            path: queryCriteria.itemPath,
            historyMode: historyMode,
            stopAtAdds: false,
            fromDate: queryCriteria.fromDate,
            toDate: queryCriteria.toDate,
            author: queryCriteria.author,
            committer: queryCriteria.user,
            skip: queryCriteria.$skip,
            maxResultCount: queryCriteria.$top
        };
        return gitHistoryArgs;
    }

    private getGitArtifactsQueryArguments(queryCriteria: GitQueryCommitsCriteria, dataOptions: GitHistoryDataOptions): GitArtifactsQueryArguments {
        const gitArtifactsQueryArguments: GitArtifactsQueryArguments = {
            fetchBuildStatuses: dataOptions.fetchBuildStatuses,
            fetchPullRequests: dataOptions.fetchPullRequests,
            fetchTags: dataOptions.fetchTags,
            startFromVersion: queryCriteria.itemVersion,
            commitIds: []
        };
        return gitArtifactsQueryArguments;
    }
}

Services.add("ICommitsDataProviderService", { serviceFactory: CommitsDataProviderService });
